import { createApi } from "@reduxjs/toolkit/query/react";
import { CollectionData, PaginationResults, TypeQuery } from "@/lib/type";
import { baseQueryWithLogout } from "@/lib/baseQuery";
import {
  MenuFormula,
  MenuFormulaComponent,
  MenuFormulaComponentFormData,
  MenuFormulaComponentItem,
  MenuFormulaComponentItemFormData,
  MenuFormulaFormData,
  FormulaComponentItemOption,
  FormulaComponentItemOptionFormData,
} from "./menu_formula.type";

export const MenuFormulaApi = createApi({
  reducerPath: "menuFormulaApi",
  tagTypes: ["menuFormula", "menuFormulas", "menuFormulasActive"],
  baseQuery: baseQueryWithLogout,
  endpoints: (build) => ({
    // --- Formulas ---

    getMenuFormulas: build.query<
      PaginationResults<MenuFormula>,
      { restaurantId: string; query?: TypeQuery }
    >({
      query: ({ restaurantId, query }) => ({
        url: `restaurants/${restaurantId}/menu_formulas`,
        method: "GET",
        params: query,
      }),
      providesTags: ["menuFormulas"],
      transformResponse: (data: CollectionData<MenuFormula>) => ({
        results: data["hydra:member"],
        count: data["hydra:totalItems"],
      }),
    }),

    getActiveMenuFormulas: build.query<MenuFormula[], string>({
      query: (restaurantId) => ({
        url: `restaurants/${restaurantId}/menu_formulas/active`,
        method: "GET",
      }),
      providesTags: ["menuFormulasActive"],
      transformResponse: (data: MenuFormula[] | CollectionData<MenuFormula>) => {
        if (Array.isArray(data)) return data;
        return data["hydra:member"] ?? [];
      },
    }),

    getMenuFormula: build.query<MenuFormula, string>({
      query: (id) => `menu_formulas/${id}`,
      providesTags: ["menuFormula"],
    }),

    createMenuFormula: build.mutation<MenuFormula, MenuFormulaFormData>({
      query: (data) => ({
        url: "menu_formulas",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["menuFormulas", "menuFormulasActive"],
    }),

    updateMenuFormula: build.mutation<
      MenuFormula,
      { id: string; data: Partial<MenuFormulaFormData> }
    >({
      query: ({ id, data }) => ({
        url: `menu_formulas/${id}`,
        method: "PATCH",
        body: JSON.stringify(data),
        headers: {
          "Content-Type": "application/merge-patch+json",
        },
      }),
      invalidatesTags: ["menuFormulas", "menuFormula", "menuFormulasActive"],
    }),

    deleteMenuFormula: build.mutation<void, string>({
      query: (id) => ({
        url: `menu_formulas/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["menuFormulas", "menuFormulasActive"],
    }),

    // --- Components ---

    createMenuFormulaComponent: build.mutation<
      MenuFormulaComponent,
      MenuFormulaComponentFormData
    >({
      query: (data) => ({
        url: "menu_formula_components",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["menuFormula", "menuFormulas", "menuFormulasActive"],
    }),

    updateMenuFormulaComponent: build.mutation<
      MenuFormulaComponent,
      { id: string; data: Partial<Omit<MenuFormulaComponentFormData, "menuFormula">> }
    >({
      query: ({ id, data }) => ({
        url: `menu_formula_components/${id}`,
        method: "PATCH",
        body: JSON.stringify(data),
        headers: {
          "Content-Type": "application/merge-patch+json",
        },
      }),
      invalidatesTags: ["menuFormula", "menuFormulas", "menuFormulasActive"],
    }),

    deleteMenuFormulaComponent: build.mutation<void, string>({
      query: (id) => ({
        url: `menu_formula_components/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["menuFormula", "menuFormulas", "menuFormulasActive"],
    }),

    // --- Component Items ---

    createMenuFormulaComponentItem: build.mutation<
      MenuFormulaComponentItem,
      MenuFormulaComponentItemFormData
    >({
      query: (data) => ({
        url: "menu_formula_component_items",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["menuFormula", "menuFormulas", "menuFormulasActive"],
    }),

    updateMenuFormulaComponentItem: build.mutation<
      MenuFormulaComponentItem,
      { id: string; data: Partial<Omit<MenuFormulaComponentItemFormData, "menuFormulaComponent">> }
    >({
      query: ({ id, data }) => ({
        url: `menu_formula_component_items/${id}`,
        method: "PATCH",
        body: JSON.stringify(data),
        headers: {
          "Content-Type": "application/merge-patch+json",
        },
      }),
      invalidatesTags: ["menuFormula", "menuFormulas", "menuFormulasActive"],
    }),

    deleteMenuFormulaComponentItem: build.mutation<void, string>({
      query: (id) => ({
        url: `menu_formula_component_items/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["menuFormula", "menuFormulas", "menuFormulasActive"],
    }),

    // --- Component Item Options ---

    createFormulaComponentItemOption: build.mutation<
      FormulaComponentItemOption,
      FormulaComponentItemOptionFormData
    >({
      query: (data) => ({
        url: "formula_component_item_options",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["menuFormula", "menuFormulas", "menuFormulasActive"],
    }),

    deleteFormulaComponentItemOption: build.mutation<void, string>({
      query: (id) => ({
        url: `formula_component_item_options/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["menuFormula", "menuFormulas", "menuFormulasActive"],
    }),
  }),
});

export const {
  useGetMenuFormulasQuery,
  useGetActiveMenuFormulasQuery,
  useGetMenuFormulaQuery,
  useCreateMenuFormulaMutation,
  useUpdateMenuFormulaMutation,
  useDeleteMenuFormulaMutation,
  useCreateMenuFormulaComponentMutation,
  useUpdateMenuFormulaComponentMutation,
  useDeleteMenuFormulaComponentMutation,
  useCreateMenuFormulaComponentItemMutation,
  useUpdateMenuFormulaComponentItemMutation,
  useDeleteMenuFormulaComponentItemMutation,
  useCreateFormulaComponentItemOptionMutation,
  useDeleteFormulaComponentItemOptionMutation,
} = MenuFormulaApi;
